import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Trophy, Eye, EyeOff, Save, Trash2, Download } from "lucide-react";
import Flag from "../../components/Flag";
import { countryName } from "../../lib/countries";

const fmt = (s) => `${(s || 0).toFixed(1)}s`;

export default function AdminRankings() {
  const [quizzes, setQuizzes] = useState([]);
  const [quizId, setQuizId] = useState("");
  const [quiz, setQuiz] = useState(null);
  const [results, setResults] = useState([]);
  const [topN, setTopN] = useState(10);
  const [savingTop, setSavingTop] = useState(false);

  const loadQuizzes = async () => {
    const { data } = await api.get("/admin/quizzes");
    const comp = (data || []).filter((q) => q.type === "competition");
    setQuizzes(comp);
    if (comp.length && !quizId) selectQuiz(comp[0], data);
  };
  useEffect(() => { loadQuizzes(); }, []);

  const selectQuiz = async (q, all) => {
    setQuizId(q.id);
    setQuiz(q);
    setTopN(q.ranking_top_n ?? 10);
    const { data } = await api.get(`/admin/quizzes/${q.id}/results`);
    setResults(data || []);
  };

  const onSelectChange = async (id) => {
    const q = quizzes.find((x) => x.id === id);
    if (q) selectQuiz(q);
  };

  const refreshResults = async (id) => {
    const { data } = await api.get(`/admin/quizzes/${id}/results`);
    setResults(data || []);
  };

  const toggleEnabled = async () => {
    const next = quiz.ranking_enabled === false; // becoming enabled if currently disabled
    try {
      const { data } = await api.put(`/admin/quizzes/${quizId}`, { ranking_enabled: next });
      setQuiz((q) => ({ ...q, ranking_enabled: next }));
      setQuizzes((qs) => qs.map((q) => (q.id === quizId ? { ...q, ranking_enabled: next } : q)));
      toast.success(next ? "Ranking disponível ao público" : "Ranking indisponível ao público");
    } catch (e) { toast.error("Não foi possível alterar"); }
  };

  const saveTopN = async () => {
    const n = Math.max(0, parseInt(topN, 10) || 0);
    setSavingTop(true);
    try {
      await api.put(`/admin/quizzes/${quizId}`, { ranking_top_n: n });
      setQuiz((q) => ({ ...q, ranking_top_n: n }));
      setQuizzes((qs) => qs.map((q) => (q.id === quizId ? { ...q, ranking_top_n: n } : q)));
      toast.success(n === 0 ? "A mostrar todos os participantes" : `Top ${n} definido`);
    } catch (e) { toast.error("Não foi possível guardar"); }
    finally { setSavingTop(false); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Eliminar este resultado? Esta ação é irreversível.")) return;
    try { await api.delete(`/admin/results/${id}`); toast.success("Resultado eliminado"); refreshResults(quizId); }
    catch { toast.error("Erro ao eliminar"); }
  };

  const exportCSV = () => {
    const headers = ["pos", "nome", "escola", "nacionalidade", "final"];
    const rows = results.map((r, i) => [i + 1, r.participant_name || "", r.school || "", countryName(r.nationality, "pt") || r.nationality || "", r.timed_out ? "Tempo esgotado" : r.final_seconds]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ranking-${quizId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const enabled = quiz?.ranking_enabled !== false;
  const cutoff = parseInt(topN, 10) || 0;

  return (
    <div data-testid="admin-rankings-page">
      <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] flex items-center gap-2 mb-2"><Trophy size={26} /> Classificações</h1>
      <p className="text-sm text-[#0B1D3A]/55 mb-6">Controla as tabelas de ranking dos concursos: escolhe o quiz, define se o ranking está disponível ao público e quantos participantes aparecem no top.</p>

      {quizzes.length === 0 ? (
        <p className="text-sm text-[#0B1D3A]/50">Não existem quizzes do tipo concurso.</p>
      ) : (
        <>
          <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
            <div>
              <label className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Concurso</label>
              <select value={quizId} onChange={(e) => onSelectChange(e.target.value)} data-testid="rankings-quiz-select"
                className="w-full px-3 py-2.5 rounded-lg border border-[#0B1D3A]/15 text-sm bg-white">
                {quizzes.map((q) => (
                  <option key={q.id} value={q.id}>{q.name_pt || q.name_en || q.id}{q.active === false ? " (inativo)" : ""}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Disponibilidade pública</label>
              <button onClick={toggleEnabled} data-testid="rankings-toggle-enabled"
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm transition-colors ${enabled ? "bg-[#2D9C5A] text-white hover:bg-[#25834b]" : "bg-[#0B1D3A]/10 text-[#0B1D3A]/70 hover:bg-[#0B1D3A]/15"}`}>
                {enabled ? <><Eye size={16} /> Ranking disponível</> : <><EyeOff size={16} /> Ranking indisponível</>}
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Alunos no top <span className="font-normal text-[#0B1D3A]/50">(0 = todos)</span></label>
              <div className="flex items-center gap-2">
                <input type="number" min="0" value={topN} onChange={(e) => setTopN(e.target.value)} data-testid="rankings-topn-input"
                  className="w-full px-3 py-2.5 rounded-lg border border-[#0B1D3A]/15 text-sm" />
                <button onClick={saveTopN} disabled={savingTop} data-testid="rankings-topn-save"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#0B1D3A] text-white font-semibold text-sm disabled:opacity-50"><Save size={15} /> Guardar</button>
              </div>
            </div>
          </div>

          <div className={`rounded-lg px-4 py-2.5 mb-4 text-sm font-semibold ${enabled ? "bg-[#2D9C5A]/10 text-[#2D9C5A]" : "bg-[#E63946]/10 text-[#E63946]"}`} data-testid="rankings-status">
            {enabled
              ? (cutoff > 0 ? `Público: mostra os primeiros ${cutoff} participantes.` : "Público: mostra todos os participantes.")
              : "Ranking ESCONDIDO do público (o site não mostra a tabela deste concurso)."}
          </div>

          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-sm text-[#0B1D3A]/60" data-testid="rankings-count">{results.length} participante(s)</span>
            <button onClick={exportCSV} disabled={results.length === 0} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0B1D3A] text-white text-sm font-semibold disabled:opacity-40" data-testid="rankings-export-csv"><Download size={14} /> Exportar CSV</button>
          </div>

          <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="rankings-table">
              <thead className="bg-[#F8FAFC] text-[#0B1D3A]/70">
                <tr>
                  <th className="text-left px-4 py-3 w-16">Posição</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Escola</th>
                  <th className="text-left px-4 py-3">Nacionalidade</th>
                  <th className="text-right px-4 py-3">Tempo final</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const beyond = cutoff > 0 && i >= cutoff && enabled;
                  return (
                    <tr key={r.id} className={`border-t border-[#0B1D3A]/5 ${beyond ? "opacity-40" : ""}`} data-testid={`rankings-row-${i}`}>
                      <td className="px-4 py-3 font-bold">{i + 1}</td>
                      <td className="px-4 py-3" data-testid={`rankings-name-${i}`}>{r.participant_name || "—"}</td>
                      <td className="px-4 py-3">{r.school || "—"}</td>
                      <td className="px-4 py-3">{r.nationality ? <Flag code={r.nationality} showName /> : "—"}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#005F73]" data-testid={`rankings-time-${i}`}>{r.timed_out ? <span className="text-[#9CA3AF] font-semibold">Tempo esgotado</span> : fmt(r.final_seconds)}</td>
                      <td className="px-4 py-3 text-right"><button onClick={() => deleteOne(r.id)} className="text-red-600 hover:text-red-700" title="Eliminar resultado" data-testid={`rankings-delete-${i}`}><Trash2 size={14} /></button></td>
                    </tr>
                  );
                })}
                {results.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-[#0B1D3A]/50">Sem participações ainda.</td></tr>}
              </tbody>
            </table>
            {cutoff > 0 && enabled && results.length > cutoff && (
              <p className="px-4 py-2 text-xs text-[#0B1D3A]/45 border-t border-dashed border-[#0B1D3A]/10">As linhas esbatidas ficam abaixo do Top {cutoff} e não aparecem no site.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
